import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, CircleDollarSign, Link2, ShieldCheck, Eye, GraduationCap, Users, Send, CheckCircle2 } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import Seo from "@/components/Seo";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";

export default function JoinSalesPage() {
  const [me, setMe] = useState(undefined); // undefined = loading, null = logged out
  const [form, setForm] = useState({ experience: "", networks: "", hours: "", pitch: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => setMe(null));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/sales/apply", form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="join-sales-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30">
      <Seo
        title="Join the Sales Team — Earn Commission on Game Projects"
        description="Become a Specpixel Entertainment sales representative. Work inbound leads or bring your own, earn 10% commission on every closed game development project, and get paid as clients pay milestones."
        path="/join-sales"
        keywords="sales rep remote, game development sales job, commission sales partner, freelance sales representative, sell game services"
      />
      <SiteNav />

      <header className="border-b border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-texture opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-24">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
            <p className="font-mono text-xs tracking-[0.4em] text-nc-orange uppercase mb-4">Recruitment // Sales Partner Program</p>
            <h1 data-testid="join-sales-title" className="font-sans font-bold uppercase tracking-tight text-3xl sm:text-5xl lg:text-6xl text-zinc-50 max-w-4xl">
              Sell AAA game services. <span className="text-nc-orange">Earn on every deal.</span>
            </h1>
            <p className="font-mono text-sm text-zinc-400 mt-6 max-w-2xl leading-relaxed">
              Specpixel Entertainment is recruiting sales representatives worldwide. Work inbound leads from our live
              queue or bring your own clients — when a deal closes, you earn commission on the full project value.
              No upfront cost. Full training provided. Everything tracked, every dollar provable.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#apply" data-testid="join-sales-apply-cta" className="font-mono text-xs tracking-[0.25em] uppercase bg-nc-orange text-black px-6 py-3.5 hover:bg-nc-orange/80 transition-colors duration-300 inline-flex items-center gap-2">
                Apply Now <ArrowRight className="w-4 h-4" />
              </a>
              <Link to="/services" className="font-mono text-xs tracking-[0.25em] uppercase border border-white/20 text-zinc-200 px-6 py-3.5 hover:border-nc-blue hover:text-nc-blue transition-colors duration-300">
                What You'll Sell
              </Link>
            </div>
          </motion.div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-14 w-full">
        {/* Earnings math */}
        <h2 className="font-sans font-bold uppercase tracking-tight text-2xl sm:text-3xl text-zinc-50 mb-8">The earning math — fully transparent</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-16">
          {[
            { icon: CircleDollarSign, t: "10% Commission", d: "Of the total project value on every deal you close. Close an AUD 8,000 project — AUD 800 gross commission." },
            { icon: ShieldCheck, t: "20% Platform Fee", d: "Specpixel retains a transparent fee on your commission — it funds the platform, escrow protection and the lead flow you work from. You net AUD 640 on that deal." },
            { icon: Link2, t: "Your Own Link", d: "Share your personal link anywhere. Prospects who arrive through it are auto-assigned to you — no claim race, same commission." },
            { icon: Users, t: "Inbound Leads Fed to You", d: "Our site generates live prospects around the clock. Claim them from the shared queue and convert." },
          ].map((c, i) => (
            <motion.div key={c.t} data-testid={`join-sales-perk-${i}`} className="border border-white/10 bg-black/40 p-6" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.06 }}>
              <c.icon className="w-5 h-5 text-nc-orange mb-3" strokeWidth={1.75} />
              <p className="font-sans font-bold uppercase text-sm text-zinc-100">{c.t}</p>
              <p className="font-mono text-[11px] text-zinc-500 mt-2 leading-relaxed">{c.d}</p>
            </motion.div>
          ))}
        </div>

        {/* How it works */}
        <h2 className="font-sans font-bold uppercase tracking-tight text-2xl sm:text-3xl text-zinc-50 mb-8">How the program works</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16">
          {[
            { n: "01", t: "Apply & Get Approved", d: "Every application is reviewed personally by the studio. Approved reps receive their account and enter the Sales Academy." },
            { n: "02", t: "Complete the Sales Academy", d: "A short, practical onboarding inside your dashboard: how you earn, working the queue, pitching the studio, scoping and closing. Finish it to unlock the lead queue." },
            { n: "03", t: "Close Deals & Get Paid", d: "Claim leads or bring your own. Commission locks on close, becomes claimable as clients pay milestones via Stripe, and pays out after admin approval." },
          ].map((s, i) => (
            <motion.div key={s.n} data-testid={`join-sales-step-${s.n}`} className="border border-white/10 bg-black/40 p-6" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.06 }}>
              <p className="font-sans font-bold text-3xl text-nc-blue/60">{s.n}</p>
              <p className="font-sans font-bold uppercase text-sm text-zinc-100 mt-3">{s.t}</p>
              <p className="font-mono text-[11px] text-zinc-500 mt-2 leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>

        {/* Transparency note */}
        <div className="border border-nc-blue/30 bg-nc-blue/5 p-8 mb-16 flex flex-col md:flex-row gap-6">
          <Eye className="w-6 h-6 text-nc-blue shrink-0" strokeWidth={1.75} />
          <div>
            <p className="font-sans font-bold uppercase text-sm text-zinc-100">Full transparency — you are logged, and so is your money</p>
            <p className="font-mono text-xs text-zinc-400 mt-3 leading-relaxed max-w-3xl">
              Every action in the program is recorded in the studio audit trail: lead claims, messages, scopes, closes and payouts.
              This protects you as much as it protects the studio — your earned commissions are permanently provable, and disputes
              are settled from records, not memory. Suspended accounts lose queue and earnings access.
            </p>
          </div>
        </div>

        {/* Application form */}
        <div id="apply" className="max-w-3xl mx-auto">
          {me === undefined ? (
            <div className="border border-white/10 p-10 text-center font-mono text-xs text-zinc-600">Checking your account…</div>
          ) : me === null ? (
            <div data-testid="join-sales-auth-gate" className="border border-nc-blue/40 bg-nc-blue/5 p-10 text-center flex flex-col items-center gap-5">
              <ShieldCheck className="w-10 h-10 text-nc-blue" strokeWidth={1.5} />
              <h2 className="font-sans font-bold uppercase text-xl text-zinc-50">Sign in to apply</h2>
              <p className="font-mono text-xs text-zinc-400 leading-relaxed max-w-md">
                Applications are tied to your Specpixel account — no separate logins, no manually issued credentials.
                Create a free account (or sign in), then come straight back here.
              </p>
              <Link to="/auth" data-testid="join-sales-signin-cta" className="font-mono text-xs tracking-[0.25em] uppercase bg-nc-blue text-white px-6 py-3.5 hover:bg-nc-blue/80 transition-colors duration-300 inline-flex items-center gap-2">
                Sign In / Create Account <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : me.role === "sales" ? (
            <div data-testid="join-sales-already" className="border border-nc-green/40 bg-nc-green/5 p-10 text-center">
              <CheckCircle2 className="w-10 h-10 text-nc-green mx-auto mb-4" strokeWidth={1.5} />
              <h2 className="font-sans font-bold uppercase text-xl text-zinc-50">You're already on the team</h2>
              <Link to="/sales" className="inline-block mt-4 font-mono text-xs tracking-[0.25em] uppercase text-nc-blue border border-nc-blue/40 px-6 py-3 hover:bg-nc-blue/10 transition-colors">Open Sales Dashboard</Link>
            </div>
          ) : me.role !== "citizen" ? (
            <div data-testid="join-sales-role-conflict" className="border border-nc-orange/40 bg-nc-orange/5 p-10 text-center">
              <h2 className="font-sans font-bold uppercase text-xl text-zinc-50">Account already has a role</h2>
              <p className="font-mono text-xs text-zinc-400 mt-3 max-w-md mx-auto">Your account is registered as <span className="text-nc-orange">{me.role}</span>. Contact the studio to switch tracks to sales.</p>
            </div>
          ) : done ? (
            <div data-testid="application-success" className="border border-nc-green/40 bg-nc-green/5 p-10 text-center">
              <CheckCircle2 className="w-10 h-10 text-nc-green mx-auto mb-4" strokeWidth={1.5} />
              <h2 className="font-sans font-bold uppercase text-xl text-zinc-50">Application received</h2>
              <p className="font-mono text-xs text-zinc-400 mt-4 leading-relaxed max-w-md mx-auto">
                The studio reviews every application personally. If you're approved, your existing account upgrades
                to the sales team — you'll get an email and enter the Sales Academy. Keep an eye on your inbox.
              </p>
            </div>
          ) : (
            <form data-testid="sales-application-form" onSubmit={submit} className="border border-white/10 bg-black/40 p-8 flex flex-col gap-5">
              <div className="flex items-center gap-3 mb-2">
                <GraduationCap className="w-5 h-5 text-nc-orange" strokeWidth={1.75} />
                <h2 className="font-sans font-bold uppercase text-xl text-zinc-50">Apply to the Sales Team</h2>
              </div>
              <div data-testid="apply-account" className="border border-white/10 bg-black/40 px-4 py-3 flex items-center gap-3">
                <ShieldCheck className="w-4 h-4 text-nc-green shrink-0" />
                <p className="font-mono text-[11px] text-zinc-300">Applying as <span className="text-zinc-100 font-bold">{me.name || me.email}</span> <span className="text-zinc-600">// {me.email}</span></p>
              </div>
              <div>
                <label className={labelCls}>Sales / community experience *</label>
                <textarea data-testid="apply-experience" required minLength={10} rows={3} className={`${inputCls} resize-none`} value={form.experience} onChange={(e) => set("experience", e.target.value)} placeholder="e.g. 2 years selling FiveM scripts in Discord communities, ran a 5k-member server…" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>Where can you find clients?</label>
                  <input data-testid="apply-networks" className={inputCls} value={form.networks} onChange={(e) => set("networks", e.target.value)} placeholder="Discords, Reddit, X, YouTube…" />
                </div>
                <div>
                  <label className={labelCls}>Hours per week *</label>
                  <input data-testid="apply-hours" required className={inputCls} value={form.hours} onChange={(e) => set("hours", e.target.value)} placeholder="e.g. 10-15" />
                </div>
              </div>
              <div>
                <label className={labelCls}>Why you? *</label>
                <textarea data-testid="apply-pitch" required minLength={10} rows={3} className={`${inputCls} resize-none`} value={form.pitch} onChange={(e) => set("pitch", e.target.value)} placeholder="Pitch yourself — why will you close deals for Specpixel?" />
              </div>
              <button data-testid="apply-submit" disabled={busy} className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-sans font-bold uppercase tracking-[0.2em] text-xs px-6 py-3.5 transition-colors duration-300 flex items-center justify-center gap-2">
                <Send className="w-4 h-4" /> {busy ? "Submitting…" : "Submit Application"}
              </button>
              <p className="font-mono text-[9px] text-zinc-600 uppercase tracking-[0.2em] text-center">
                No fees to join. Commission-only. Approval upgrades this account — every action logged.
              </p>
            </form>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
